import React, { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Form, FormGroup, Label, Input, Badge } from 'reactstrap'
import { getIdData, addData, updateData } from '../../lib/activitylist'
import { StudentTopMenuLayout } from '../../Layout/StudentTopMenu/StudentTopMenuLayout'
import {
	ClassForLessonSelectForActivities,
	ActivityTierSelect,
	ActivityRowStatusSelect,
	CategorySelectForActivity,
	PositionSelectForActivity
} from '../Common/FormSelects'

const InstructorStudentActivityEdit = () => {
	const [gradelevel, setGradelevel] = useState()
	const [hoursperweek, setHoursperweek] = useState()
	const [weeksperyear, setWeeksperyear] = useState()
	const [recoglevel, setRecoglevel] = useState()
	const [sortOrder, setSortOrder] = useState(0)
	const [category, setCategory] = useState()
	const [position, setPosition] = useState()
	const [description, setDescription] = useState()
	const [activityTitle, setActivityTitle] = useState('')
	const [lineStatus, setLineStatus] = useState('New')
	const [loading, setLoading] = useState(false)

	const navigate = useNavigate()
	const { studentId, status: routeMode, id } = useParams()

	const getData = async () => {
		try {
			setLoading(true)
			const organizationData = await getIdData(id)
			if (organizationData && organizationData.length > 0) {
				const row = organizationData[0]
				setActivityTitle(row.title || '')
				setLineStatus(row.status || 'New')
				setCategory(row.category || '')
				setPosition(row.position || '')
				setGradelevel(row.gradelevel || '')
				setRecoglevel(row.recoglevel || '')
				setHoursperweek(row.hoursperweek || '')
				setWeeksperyear(row.weeksperyear || '')
				setSortOrder(row.sortorder || 0)
				setDescription(row.description || '')
			}
		} catch (error) {
			console.error('Error fetching activity:', error)
		} finally {
			setLoading(false)
		}
	}

	const handleSubmit = async (e) => {
		e.preventDefault()
		setLoading(true)

		try {
			const formData = {
				studentId: studentId,
				title: (activityTitle || '').slice(0, 50),
				status: lineStatus || 'New',
				gradelevel,
				recoglevel,
				hoursperweek,
				weeksperyear,
				category,
				position,
				sortorder: sortOrder,
				description
			}

			if (routeMode === 'Add') {
				await addData(formData)
			} else {
				await updateData({ id, ...formData })
			}

			navigate(-1)
		} catch (error) {
			console.error('Error saving activity:', error)
		} finally {
			setLoading(false)
		}
	}

	useEffect(() => {
		if (routeMode === 'Update' && id) {
			getData()
		}
	}, [routeMode, id, studentId])

	return (
		<StudentTopMenuLayout activeMenu="Activity" pageName="ActivityEdit" showPageNotes={false}>
			<div className="card mb-4 shadow-sm" style={{ borderRadius: '8px' }}>
				<div className="card-header bg-white border-bottom">
					<h5 className="mb-0 text-dark">{routeMode === 'Add' ? 'Add Activity' : 'Update Activity'}</h5>
				</div>
				<div className="card-body bg-white">
					{loading && routeMode === 'Update' ? (
						<div className="text-center p-4">
							<div className="spinner-border" role="status">
								<span className="visually-hidden">Loading...</span>
							</div>
						</div>
					) : (
						<Form onSubmit={handleSubmit}>
							<div className="row">
								<div className="col-lg-9 col-md-12">
									<FormGroup className="mb-3">
										<Label className="form-control-label fw-bold mb-2">Title</Label>
										<Input
											type="text"
											maxLength={50}
											value={activityTitle}
											onChange={(e) => setActivityTitle(e.target.value)}
											className="form-control border-radius-10"
											placeholder="Title (max 50 characters)"
											style={{
												border: '1px solid #e9ecef',
												boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
												height: '45px',
												padding: '10px 15px'
											}}
										/>
										<small className="text-muted">{activityTitle.length}/50</small>
									</FormGroup>

									<FormGroup className="mb-3">
										<ActivityRowStatusSelect value={lineStatus} setValue={setLineStatus} />
									</FormGroup>

									<FormGroup className="mb-3">
										<ClassForLessonSelectForActivities value={gradelevel} setValue={setGradelevel} />
									</FormGroup>

									<FormGroup className="mb-3">
										<Label className="form-control-label fw-bold mb-2">Hours Per Week</Label>
										<Input
											type="number"
											value={hoursperweek}
											onChange={(e) => setHoursperweek(e.target.value)}
											className="form-control border-radius-10"
											placeholder="Hours Per Week"
											min="0"
											style={{
												border: '1px solid #e9ecef',
												boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
												height: '45px',
												padding: '10px 15px'
											}}
										/>
									</FormGroup>

									<FormGroup className="mb-3">
										<Label className="form-control-label fw-bold mb-2">Weeks Per Year</Label>
										<Input
											type="number"
											value={weeksperyear}
											onChange={(e) => setWeeksperyear(e.target.value)}
											className="form-control border-radius-10"
											placeholder="Weeks Per Year"
											min="0"
											style={{
												border: '1px solid #e9ecef',
												boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
												height: '45px',
												padding: '10px 15px'
											}}
										/>
									</FormGroup>

									<FormGroup className="mb-3">
										<ActivityTierSelect value={recoglevel} setValue={setRecoglevel} />
									</FormGroup>

									<FormGroup className="mb-3">
										<Label className="form-control-label fw-bold mb-2">Category</Label>
										<CategorySelectForActivity value={category} setValue={setCategory} hideLabel />
									</FormGroup>

									<FormGroup className="mb-3">
										<Label className="form-control-label fw-bold mb-2">Position</Label>
										<PositionSelectForActivity value={position} setValue={setPosition} hideLabel />
									</FormGroup>

									<FormGroup className="mb-3">
										<Label className="form-control-label fw-bold mb-2">Description</Label>
										<div className="position-relative">
											<Input
												type="textarea"
												rows={4}
												value={description}
												onChange={(e) => setDescription(e.target.value)}
												className="form-control border-radius-10"
												placeholder="Description"
												style={{
													resize: 'vertical',
													minHeight: '100px',
													paddingBottom: '2.5rem',
													border: '1px solid #e9ecef',
													boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
												}}
											/>
											<Badge color="secondary" pill className="position-absolute" style={{ bottom: '10px', right: '12px', fontSize: '0.75rem' }}>
												{(description || '').length}
											</Badge>
										</div>
									</FormGroup>

									<FormGroup className="mb-3">
										<Label className="form-control-label fw-bold mb-2">Order</Label>
										<Input
											type="number"
											value={sortOrder}
											onChange={(e) => setSortOrder(e.target.value)}
											className="form-control border-radius-10"
											required
											style={{
												border: '1px solid #e9ecef',
												boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
												height: '45px',
												padding: '10px 15px'
											}}
										/>
									</FormGroup>

									<div className="d-flex justify-content-between mt-4">
										<button type="submit" className="btn btn-primary" disabled={loading}>
											<i className="fa fa-save me-2"></i>
											{loading ? 'Saving...' : routeMode === 'Add' ? 'Add Activity' : 'Update Activity'}
										</button>
										<button type="button" onClick={() => navigate(-1)} className="btn btn-secondary">
											Cancel
										</button>
									</div>
								</div>
							</div>
						</Form>
					)}
				</div>
			</div>
		</StudentTopMenuLayout>
	)
}

export { InstructorStudentActivityEdit }
